import { useCallback } from 'react';
import { useSelector } from 'src/store';

export type OneOrAll = 'one' | 'all';

/**
 * React hook that requires an authenticated user, it uses the global features returned from /user call,
 * You can use the functions returned by it to determine if to render a feature or not
 * Think in-house role-based access/feature flags
 * @returns an object you want to destructure
 */
export const useWorkspaceUserFeatures = () => {
  const { features } = useSelector((state) => state.workspaceUser);

  /**
   * Import `feature` which has nested keys
   * Then pass that as an array to `shouldFeatureRender` to assert
   * @param oneOrAll optional argument to choose whether to check for one or all permissions if present
   * @example shouldFeatureRender([feature.core.activity])
   * @returns true when user has sufficient permissions, false otherwise
   */
  const shouldFeatureRender = useCallback(
    (featuresToCheck: string[] = [], oneOrAll: 'one' | 'all' = 'one') => {
      if (!featuresToCheck || featuresToCheck.length === 0) {
        return true;
      }

      if (oneOrAll === 'one') {
        return featuresToCheck.some((featureToCheck) => {
          const splitFeature = featureToCheck.split('.');
          return features[splitFeature[0]][splitFeature[1]];
        });
      } else {
        return featuresToCheck.every((featureToCheck) => {
          const splitFeature = featureToCheck.split('.');

          return features[splitFeature[0]][splitFeature[1]];
        });
      }
    },
    [features]
  );

  return { features, shouldFeatureRender };
};
